import "./cancel-order-button.scss";
import { Button, ButtonVariant } from "@amzn/stencil-react-components/button";
import { RenderModalFunctionParams, WithModal } from "@amzn/stencil-react-components/modal";
import { withTooltip } from "@amzn/stencil-react-components/tooltip";
import React, { FC } from "react";
import { CancelOrderModal } from "./cancel-order-modal";
import {
  CancellationReason,
  CancellationState,
  OrderDetails,
  isCancellableStatus,
  isHopCancellable,
  shouldHideCancelButton,
} from "@/services/orders/cancellation.types";
import { OrderItem } from "@/services/orders/orders.types";

const ButtonWithTooltip = withTooltip()(Button);

const BUTTON_LABEL = "Cancel order";
const DISABLED_TOOLTIP = "This order can no longer be cancelled";
const HOP_DISABLED_TOOLTIP = "Order is being fulfilled and cannot be cancelled";

interface CancelOrderButtonProps {
  orderStatus?: string;
  orderId: string;
  orderDetails: OrderDetails;
  cancellationState: CancellationState;
  onConfirmCancellation: (reason: CancellationReason) => void;
  orderItems?: OrderItem[];
}

export const CancelOrderButton: FC<CancelOrderButtonProps> = ({
  orderStatus,
  orderId,
  orderDetails,
  cancellationState,
  onConfirmCancellation,
  orderItems,
}) => {
  if (shouldHideCancelButton(orderStatus)) {
    return null;
  }

  const isCancellable = isCancellableStatus(orderStatus);
  const hopCancellable = isHopCancellable(orderItems);

  const buttonClassName = `cancel-order-button--destructive${
    !isCancellable || !hopCancellable ? " cancel-order-button--disabled" : ""
  }`;

  if (!isCancellable || !hopCancellable) {
    return (
      <ButtonWithTooltip
        variant={ButtonVariant.Secondary}
        disabled
        className={buttonClassName}
        aria-label={`Cancel order ${orderId}`}
        data-testid="cancel-order-button"
        tooltipText={!isCancellable ? DISABLED_TOOLTIP : HOP_DISABLED_TOOLTIP}
      >
        {BUTTON_LABEL}
      </ButtonWithTooltip>
    );
  }

  const renderModal = ({ close }: RenderModalFunctionParams) => (
    <CancelOrderModal
      onClose={close}
      onConfirm={(reason: CancellationReason) => {
        onConfirmCancellation(reason);
      }}
      orderDetails={orderDetails}
      cancellationState={cancellationState}
    />
  );

  return (
    <WithModal renderModal={renderModal}>
      {({ open }) => (
        <Button
          variant={ButtonVariant.Secondary}
          onClick={open}
          className={buttonClassName}
          aria-label={`Cancel order ${orderId}`}
          data-testid="cancel-order-button"
          disabled={!isCancellable}
        >
          {BUTTON_LABEL}
        </Button>
      )}
    </WithModal>
  );
};
